import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Button } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { medicsService } from '../../../services/medicsService';
import { MedicTableViewDto } from '../../../models/medics/medicTableViewDto';

interface MedicsAutocompleteFormControlProps {
  name: string;
  label?: string;
  style?: React.CSSProperties;
  required?: boolean;
  disabled?: boolean;
  showSelectAllButton?: boolean;
}

const MedicsAutocompleteFormControl: React.FC<MedicsAutocompleteFormControlProps> = ({ 
  name, 
  label = 'Медицинский работник', 
  style, 
  required,
  disabled = false,
  showSelectAllButton = false
}) => {
  const { control } = useFormContext();
  const [options, setOptions] = useState<MedicTableViewDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [searchText, setSearchText] = useState('');

  const getOptionLabel = (option: MedicTableViewDto) =>
    `${option.lastName} ${option.firstName} ${option.middleName || ''}`.trim();

  const sortOptions = (items: MedicTableViewDto[]) =>
    [...items].sort((a, b) => getOptionLabel(a).localeCompare(getOptionLabel(b), 'ru'));

  useEffect(() => {
    setLoading(true);
    const filter: any = {
      page: 0,
      pageSize: 1000,
      hideDismissed: true,
      fio: searchText || undefined,
    };
    
    medicsService.getByFilter(filter)
      .then(r => setOptions(sortOptions(r.data.data.items)))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [searchText]);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? 'Обязательное поле' : false }}
      render={({ field, fieldState }) => (
        <Autocomplete
          multiple
          fullWidth
          options={options}
          getOptionLabel={getOptionLabel}
          getOptionKey={(option) => option.id}
          value={field.value || []}
          onChange={(_, value) => field.onChange(value)}
          onInputChange={(_, newInputValue) => {
            setSearchText(newInputValue);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
              style={style}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              required={required}
              disabled={disabled}
              InputLabelProps={{
                sx: {
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  maxWidth: 'none'
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {showSelectAllButton ? (
                      <Button
                        size="small"
                        sx={{ mr: 1, flexShrink: 0, textTransform: 'none' }}
                        disabled={disabled || loadingAll}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try {
                            setLoadingAll(true);
                            const filter: any = {
                              page: 0,
                              pageSize: 1000,
                              hideDismissed: true,
                            };
                            const r = await medicsService.getByFilter(filter);
                            field.onChange(sortOptions(r.data.data.items));
                          } catch {
                            // ignore - keep current selection
                          } finally {
                            setLoadingAll(false);
                          }
                        }}
                      >
                        Все
                      </Button>
                    ) : null}
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          size="small"
          disabled={disabled}
          loading={loading || loadingAll}
        />
      )}
    />
  );
};

export default MedicsAutocompleteFormControl;

